/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Hook for AI Builder execution using WebSocket streaming.
 * 
 * This hook uses the AgentChatConsumer WebSocket for real-time streaming
 * of agent executions, eliminating the need for HTTP polling.
 * 
 * WebSocket URL: ws://localhost:8000/ws/agents/<agent_id>/chat/
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  useAgentChatSocket,
  type AgentStatusUpdate,
  type BuilderPlanSnapshotContext,
  type ToolTraceMetadata,
} from './useAgentChatSocket';
import {
  isBlockedLifecycleStatus,
  normalizeBuilderLifecycleEvent,
  normalizeBuilderPlanSnapshot,
  summarizeBlueprintCompileOperation,
  type BuilderLifecycleBlocker,
  type BuilderLifecycleEvent,
  type BuilderLifecycleOperation,
  type BuilderLifecycleOperationCounts,
  type BuilderLifecycleStatus,
} from '../components/system/builderLifecycle';
import { agentApi } from '../services/api';
import type { ExpertProgress } from './useAISystemSocket';

export interface BuilderExecutionActivity {
  stage: string;
  message: string;
  detail: string | null;
  updatedAt: string;
}

const DEFAULT_ACTIVITY = {
  stage: 'preparing',
  message: 'Preparing your request',
  detail: 'Preparing the Architect handoff.',
};

// ---------------------------------------------------------------------------
// Builder artifact lifecycle projection (backend-authored; no phase inference)
// ---------------------------------------------------------------------------

/** One entry of the live operation journal (apply progress). */
export interface BuilderLifecycleOperationEntry extends BuilderLifecycleOperation {
  /** Human label shared with the saved-plan views. */
  label: string;
  occurredAt: string;
}

export interface BuilderLifecycleOperationsState extends BuilderLifecycleOperationCounts {
  entries: BuilderLifecycleOperationEntry[];
}

/**
 * Projection of the backend-owned `BuilderPlanArtifact` lifecycle. Built
 * exclusively from `builder_lifecycle` events and `builder_plan` snapshots —
 * the client never infers lifecycle state from tool calls or status keywords.
 */
export interface BuilderLifecycleProgress {
  artifactId: string | null;
  planId: string | null;
  sessionId: string | null;
  executionId: string | null;
  status: BuilderLifecycleStatus;
  previousStatus: BuilderLifecycleStatus | null;
  revision: number;
  posture: string | null;
  detail: string | null;
  operations: BuilderLifecycleOperationsState;
  blockers: BuilderLifecycleBlocker[];
  startedAt: string;
  updatedAt: string;
  occurredAt: string;
  occurredAtMs: number;
}

const EMPTY_LIFECYCLE_OPERATIONS: BuilderLifecycleOperationsState = {
  total: 0,
  completed: 0,
  failed: 0,
  entries: [],
};

function toOperationEntry(
  operation: BuilderLifecycleOperation,
  occurredAt: string
): BuilderLifecycleOperationEntry {
  return {
    ...operation,
    label: summarizeBlueprintCompileOperation({
      mutation: operation.mutation ?? undefined,
      tool_name: operation.toolName ?? undefined,
      symbolic_key: operation.symbolicKey ?? undefined,
    }),
    occurredAt,
  };
}

function countEntries(entries: BuilderLifecycleOperationEntry[]): BuilderLifecycleOperationCounts {
  const completed = entries.filter((entry) => entry.success).length;
  return { total: entries.length, completed, failed: entries.length - completed };
}

function mergeOperations(
  previous: BuilderLifecycleOperationsState,
  counts: BuilderLifecycleOperationCounts,
  lastOperation: BuilderLifecycleOperation | null,
  occurredAt: string
): BuilderLifecycleOperationsState {
  const entries = [...previous.entries];
  if (lastOperation) {
    const entry = toOperationEntry(lastOperation, occurredAt);
    const alreadyJournaled = Boolean(
      entry.operationId &&
        entries.some((existing) => existing.operationId === entry.operationId)
    );
    if (!alreadyJournaled) {
      entries.push(entry);
    }
  }
  const fromEntries = countEntries(entries);
  return {
    total: Math.max(counts.total, fromEntries.total),
    completed: Math.max(counts.completed, fromEntries.completed),
    failed: Math.max(counts.failed, fromEntries.failed),
    entries,
  };
}

function sessionMatches(
  knownSessionId: string | null,
  incomingSessionId: string | null
): boolean {
  if (!knownSessionId || !incomingSessionId) {
    return true;
  }
  return knownSessionId === incomingSessionId;
}

/**
 * Stale-event guard: events must not regress the projection. Ordering key is
 * (`rendered_revision`, `occurred_at`) per artifact — an event older than the
 * current state on either axis is a duplicate or out-of-order delivery and is
 * dropped (the same object reference is returned).
 */
function isStaleLifecycleEvent(state: BuilderLifecycleProgress, event: BuilderLifecycleEvent): boolean {
  if (event.renderedRevision !== null && event.renderedRevision < state.revision) {
    return true;
  }
  if (event.renderedRevision === null || event.renderedRevision === state.revision) {
    if (state.occurredAtMs > 0 || event.occurredAtMs > 0) {
      return event.occurredAtMs <= state.occurredAtMs;
    }
    return true;
  }
  return false;
}

/**
 * Pure reducer: apply a raw `builder_lifecycle` WebSocket payload to the
 * lifecycle projection. Invalid payloads, cross-session events, and stale or
 * out-of-order events return the previous state unchanged.
 *
 * Session semantics: `activeSessionId` is the chat session the socket is
 * currently tracking. Events for any other session are dropped (agent-room
 * fan-out). When the event belongs to the active session but the projection
 * holds a different one, the projection is stale — loading a historical
 * session does not reset it — so the event continues instead of being
 * dropped: a new artifact starts a fresh lifecycle, and the same artifact
 * keeps its journal across the session switch.
 */
export function applyLifecycleEvent(
  state: BuilderLifecycleProgress | null,
  rawEvent: unknown,
  activeSessionId?: string | null
): BuilderLifecycleProgress | null {
  const event = normalizeBuilderLifecycleEvent(rawEvent);
  if (!event) {
    return state;
  }

  if (activeSessionId && event.sessionId && event.sessionId !== activeSessionId) {
    return state;
  }

  if (
    state
    && !sessionMatches(state.sessionId, event.sessionId)
    && !(event.sessionId && event.sessionId === activeSessionId)
  ) {
    return state;
  }

  const isNewArtifact = Boolean(
    state && event.artifactId && state.artifactId && event.artifactId !== state.artifactId
  );

  // Late SUPERSEDED/ABANDONED events for an artifact we are no longer tracking
  // must not clobber the current projection.
  if (state && isNewArtifact && (event.status === 'SUPERSEDED' || event.status === 'ABANDONED')) {
    return state;
  }

  if (state && !isNewArtifact && isStaleLifecycleEvent(state, event)) {
    return state;
  }

  const base: BuilderLifecycleProgress | null = isNewArtifact ? null : state;
  const occurredAt = event.occurredAt || base?.occurredAt || '';
  const operations = mergeOperations(
    base?.operations ?? EMPTY_LIFECYCLE_OPERATIONS,
    event.operations,
    event.lastOperation,
    event.occurredAt
  );
  const blockers: BuilderLifecycleBlocker[] =
    event.blockers.length > 0
      ? event.blockers
      : isBlockedLifecycleStatus(event.status)
        ? base?.blockers ?? []
        : [];

  return {
    artifactId: event.artifactId ?? base?.artifactId ?? null,
    planId: event.planId ?? base?.planId ?? null,
    sessionId: event.sessionId ?? base?.sessionId ?? null,
    executionId: event.executionId ?? base?.executionId ?? null,
    status: event.status,
    previousStatus: event.previousStatus ?? base?.status ?? null,
    revision: event.renderedRevision ?? base?.revision ?? 0,
    posture: event.promptPosture ?? base?.posture ?? null,
    detail: event.detail ?? null,
    operations,
    blockers,
    startedAt: base?.startedAt || occurredAt,
    updatedAt: occurredAt,
    occurredAt,
    occurredAtMs: event.occurredAtMs || base?.occurredAtMs || 0,
  };
}

/**
 * Hydrate the lifecycle projection from a `builder_plan` snapshot carried by
 * `execution_started` / `connected` / `subscribed` messages. Produces the same
 * state shape as accumulated events so late subscribers and live listeners
 * render identically. Older or same-revision snapshots never destroy journal
 * entries already accumulated from live events. Session semantics match
 * `applyLifecycleEvent`: snapshots for the active session replace a stale
 * projection left over from a previously loaded session.
 */
export function hydrateLifecycleFromSnapshot(
  state: BuilderLifecycleProgress | null,
  rawSnapshot: unknown,
  context?: Partial<BuilderPlanSnapshotContext> | null,
  activeSessionId?: string | null
): BuilderLifecycleProgress | null {
  const snapshot = normalizeBuilderPlanSnapshot(rawSnapshot, context);
  if (!snapshot) {
    return state;
  }

  if (activeSessionId && snapshot.sessionId && snapshot.sessionId !== activeSessionId) {
    return state;
  }

  if (
    state
    && !sessionMatches(state.sessionId, snapshot.sessionId)
    && !(snapshot.sessionId && snapshot.sessionId === activeSessionId)
  ) {
    return state;
  }

  const sameArtifact = !state
    || !snapshot.artifactId
    || !state.artifactId
    || snapshot.artifactId === state.artifactId;

  if (state && sameArtifact) {
    if (snapshot.renderedRevision !== null && snapshot.renderedRevision < state.revision) {
      return state;
    }
    if (snapshot.renderedRevision === null || snapshot.renderedRevision === state.revision) {
      // Same lifecycle position: keep accumulated entries, reconcile counters.
      return {
        ...state,
        sessionId: snapshot.sessionId ?? state.sessionId,
        status: snapshot.status,
        blockers: snapshot.blockers.length > 0 ? snapshot.blockers : state.blockers,
        posture: snapshot.promptPosture ?? state.posture,
        operations: {
          total: Math.max(snapshot.operations.total, state.operations.total),
          completed: Math.max(snapshot.operations.completed, state.operations.completed),
          failed: Math.max(snapshot.operations.failed, state.operations.failed),
          entries: state.operations.entries,
        },
      };
    }
  }

  return {
    artifactId: snapshot.artifactId,
    planId: snapshot.planId,
    sessionId: snapshot.sessionId ?? state?.sessionId ?? null,
    executionId: snapshot.executionId ?? state?.executionId ?? null,
    status: snapshot.status,
    previousStatus: null,
    revision: snapshot.renderedRevision ?? state?.revision ?? 0,
    posture: snapshot.promptPosture,
    detail: null,
    operations: { ...snapshot.operations, entries: [] },
    blockers: snapshot.blockers,
    startedAt: snapshot.occurredAt,
    updatedAt: snapshot.occurredAt,
    occurredAt: snapshot.occurredAt,
    occurredAtMs: 0,
  };
}

/**
 * Extract the blocking issue reported by the durable runtime payload. The
 * runtime event is real backend signal (not phase inference): only the issue
 * text is surfaced; lifecycle state comes exclusively from
 * `builder_lifecycle` events.
 */
function runtimeLastIssue(
  runtime: Record<string, any> | null | undefined
): string | null {
  if (!runtime || typeof runtime !== 'object') return null;
  const issue =
    runtime.last_blocking_issue
    || runtime.blocking_issue
    || runtime.error
    || runtime.reason
    || null;
  return issue ? String(issue) : null;
}

function createActivity(
  stage: string,
  message: string,
  detail: string | null = null
): BuilderExecutionActivity {
  return {
    stage,
    message,
    detail,
    updatedAt: new Date().toISOString(),
  };
}

function buildActivityFromStatus(
  status: AgentStatusUpdate,
  fallback = DEFAULT_ACTIVITY
): BuilderExecutionActivity {
  return createActivity(
    status.stage || fallback.stage,
    status.message || fallback.message,
    status.detail || fallback.detail || null,
  );
}

function formatToolActivity(toolName?: string): string {
  if (!toolName) return 'Checking data';
  return toolName
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

export interface UseAIBuilderExecutionOptions {
  /** AI System ID */
  systemId: number;
  /** Agent ID for WebSocket connection */
  agentId: number;
  /** Whether to auto-connect WebSocket (default: true) */
  autoConnect?: boolean;
  /** Callback when execution completes */
  onExecutionCompleted?: (executionId: string, status: string) => void;
  /** Callback when error occurs */
  onError?: (error: string) => void;
  /** Callback for tool calls */
  onToolCall?: (
    toolName: string,
    toolArgs: Record<string, any>,
    toolCallId?: string,
    trace?: ToolTraceMetadata
  ) => void;
  /** Callback for tool results */
  onToolResult?: (
    toolName: string,
    result: any,
    toolCallId?: string,
    trace?: ToolTraceMetadata
  ) => void;
  /** Callback for LLM response chunks */
  onLLMResponse?: (content: string, isFinal: boolean) => void;
  /** Callback for nested sub-agent events (source === 'sub_agent'). */
  onSubAgentEvent?: (event: any) => void;
}

export interface UseAIBuilderExecutionReturn {
  /** Whether WebSocket is connected */
  isConnected: boolean;
  /** Whether an execution is currently running */
  isExecuting: boolean;
  /** Whether a request has been sent and is waiting for execution_started */
  isAwaitingExecutionStart: boolean;
  /** Current execution ID */
  currentExecutionId: string | null;
  /** Current session ID from WebSocket */
  currentSessionId: string | null;
  /** Current execution plan from the live WebSocket execution_started event */
  currentExecutionPlan: Record<string, any> | null;
  /** Execution status */
  executionStatus: string | null;
  /** Progress percentage (based on iterations) */
  progress: number;
  /** Current iteration */
  currentIteration: number;
  /** Max iterations allowed */
  maxIterations: number;
  /** Expert progress tracking (for compatibility) */
  expertProgress: ExpertProgress[];
  /** Error message */
  error: string | null;
  /** Total cost */
  totalCost: number;
  /** Total tokens used */
  totalTokens: number;
  /** Tool names available */
  toolNames: string[];
  /** Execution result/response */
  executionResult: any | null;
  /** Current human-readable execution activity */
  assistantActivity: BuilderExecutionActivity | null;
  /** Last blocking issue reported by the durable runtime payload (issue text only) */
  lastIssue: string | null;
  /** Backend-authored artifact lifecycle projection (real statuses, no inference) */
  lifecycleProgress: BuilderLifecycleProgress | null;
  /** Start execution with message via WebSocket */
  executeWithMessage: (message: string, sessionId?: string | null, context?: Record<string, any>, extraPayload?: Record<string, any>) => Promise<{ id: string; session_id?: string }>;
  /** Stop the current execution */
  stopExecution: () => Promise<void>;
  /** Subscribe to an existing execution (for historical sessions) */
  subscribeToExecution: (executionId: string) => void;
  /** Reset state for new conversation */
  reset: () => void;
  /** Connect to WebSocket */
  connect: () => void;
  /** Disconnect from WebSocket */
  disconnect: () => void;
  /** Pending confirmation data for dangerous tools */
  pendingConfirmation: {
    executionId: string;
    toolName: string;
    toolArgs: Record<string, any>;
    toolCallId: string;
    dangerLevel: number;
    message: string;
  } | null;
  /** Confirm or deny a dangerous tool */
  confirmTool: (executionId: string, toolName: string, confirmed: boolean) => boolean;
}

/**
 * Hook that uses WebSocket streaming for AI Builder agent executions.
 * 
 * Connects to the AgentChatConsumer WebSocket for real-time updates
 * on tool calls, LLM responses, and execution status.
 */
/**
 * Serialized execute frame for the agent WebSocket, shared by every caller.
 * Extra payload entries (e.g. the validated operating selection) ride at the
 * top level exactly as the REST contract spells them.
 */
export function buildAgentExecutePayload({
  message, sessionId, systemId, context, extraPayload,
}: {
  message: string;
  sessionId?: string | null;
  systemId: number;
  context?: Record<string, any>;
  extraPayload?: Record<string, any>;
}): Record<string, any> {
  const payload: Record<string, any> = {
    type: 'execute',
    message,
    ...(extraPayload ?? {}),
  };
  if (sessionId) {
    payload.session_id = sessionId;
  }
  payload.ai_system_id = systemId;
  payload.context = {
    ...context,
    system_id: systemId,
  };
  payload.max_iterations = 50;
  return payload;
}

export function useAIBuilderExecution(options: UseAIBuilderExecutionOptions): UseAIBuilderExecutionReturn {
  const {
    systemId,
    agentId,
    autoConnect = true,
    onExecutionCompleted,
    onError,
    onToolCall,
    onToolResult,
    onLLMResponse,
    onSubAgentEvent,
  } = options;

  // Track pending execution promise
  const pendingExecutionRef = useRef<{
    resolve: (value: { id: string; session_id?: string }) => void;
    reject: (error: Error) => void;
  } | null>(null);

  // Track the message being sent (for promise resolution)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [assistantActivity, setAssistantActivity] = useState<BuilderExecutionActivity | null>(null);
  /** Last blocking issue from the durable runtime payload (sticky until the next run). */
  const [lastIssue, setLastIssue] = useState<string | null>(null);
  // Backend-authored lifecycle projection (builder_lifecycle events + snapshots)
  const [lifecycleProgress, setLifecycleProgress] = useState<BuilderLifecycleProgress | null>(null);

  // Latest socket session ID for lifecycle session filtering (stable ref so
  // socket callbacks always read the current value without stale closures).
  const activeSessionIdRef = useRef<string | null>(null);

  // Expert progress (for compatibility with existing UI)
  const [expertProgress] = useState<ExpertProgress[]>([]);

  // Polling fallback for when WebSocket is not available
  // NOTE: Defined early so it can be used in socket callbacks
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Track restored execution ID in a ref (to avoid stale closure issues)
  const restoredExecutionIdRef = useRef<string | null>(null);
  
  // Track if we've already sent subscribe for this execution (to avoid duplicates)
  const hasSubscribedRef = useRef<string | null>(null);
  
  // Track completions already handled across stop/reset races.
  const handledCompletionIdsRef = useRef<Set<string>>(new Set());
  
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      console.log('[AIBuilderExecution] Stopping polling');
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  // Use the agent chat socket
  const socket = useAgentChatSocket({
    agentId,
    autoConnect,
    onConnected: (agentName) => {
      console.log('[AIBuilderExecution] Connected to agent:', agentName);
      
      // Check if the backend auto-subscribed us to a running execution
      // (this happens when the backend detects a running execution on connect)
      const autoSubExecId = socketRef.current.executionId;
      if (autoSubExecId && socketRef.current.isExecuting) {
        console.log('[AIBuilderExecution] Backend auto-subscribed to running execution:', autoSubExecId);
        // The backend already added us to the execution group, so we'll receive
        // real-time WebSocket updates. Stop polling if it was active.
        stopPolling();
        setAssistantActivity(createActivity(
          'running',
          'Resuming the active run',
          'Background build steps are still running for this request.',
        ));
        restoredExecutionIdRef.current = autoSubExecId;
        hasSubscribedRef.current = autoSubExecId;
        return;
      }
      
      // If we have a restored execution and haven't subscribed yet, send subscribe message
      if (restoredExecutionIdRef.current && hasSubscribedRef.current !== restoredExecutionIdRef.current) {
        const execId = restoredExecutionIdRef.current;
        console.log('[AIBuilderExecution] Sending subscribe for restored execution:', execId);
        hasSubscribedRef.current = execId; // Mark as subscribed
        // Use setTimeout to ensure socketRef is updated
        setTimeout(() => {
          socketRef.current.sendRaw({
            type: 'subscribe',
            execution_id: execId,
          });
        }, 0);
      }
    },
    onDisconnected: () => {
      console.log('[AIBuilderExecution] Disconnected from agent');
    },
    onReconnected: async (wasExecuting, executionId) => {
      console.log('[AIBuilderExecution] Reconnected! wasExecuting:', wasExecuting, 'executionId:', executionId);
      if (wasExecuting) {
        setAssistantActivity(createActivity(
          'running',
          'Resuming the Architect run',
          'Reconnected to the active execution.',
        ));
      }
      
      // If we have a restored execution, re-subscribe (need to rejoin group after reconnect)
      if (restoredExecutionIdRef.current) {
        const execId = restoredExecutionIdRef.current;
        console.log('[AIBuilderExecution] Re-subscribing for restored execution on reconnect:', execId);
        // Reset subscription flag since this is a new connection
        hasSubscribedRef.current = execId;
        setTimeout(() => {
          socketRef.current.sendRaw({
            type: 'subscribe',
            execution_id: execId,
          });
        }, 0);
      }
      
      // If we were executing when disconnected (not a restored execution), fetch latest state from API
      if (wasExecuting && executionId) {
        try {
          console.log('[AIBuilderExecution] Fetching execution state from API after reconnect...');
          const execution = await agentApi.getExecutionSummary(executionId);
          
          if (execution.status === 'COMPLETED' || execution.status === 'ERROR' || execution.status === 'CANCELLED') {
            // Execution finished while we were disconnected
            console.log('[AIBuilderExecution] Execution finished while disconnected:', execution.status);
            onExecutionCompleted?.(executionId, execution.status);
          } else {
            console.log('[AIBuilderExecution] Execution still running, WebSocket will resume streaming');
          }
        } catch (error) {
          console.error('[AIBuilderExecution] Failed to sync execution state:', error);
        }
      }
    },
    onSubscribed: (executionId, status, isStreaming) => {
      console.log('[AIBuilderExecution] Subscribed to execution:', executionId, 'status:', status, 'streaming:', isStreaming);
      
      if (isStreaming) {
        // WebSocket will receive updates, stop polling
        console.log('[AIBuilderExecution] WebSocket streaming available, stopping polling');
        stopPolling();
      } else {
        // No active task in backend, execution might have finished or we need to poll
        console.log('[AIBuilderExecution] No WebSocket streaming, continuing with polling');
      }
      
      if (status !== 'RUNNING') {
        // Execution already finished
        restoredExecutionIdRef.current = null;
        stopPolling();
      }
    },
    onExecutionStarted: (executionId, sessionId) => {
      console.log('[AIBuilderExecution] Execution started:', executionId, 'session:', sessionId);
      setAssistantActivity(createActivity(
        'running',
        'Starting the Architect run',
        'The AI worker picked up your request.',
      ));
      setLastIssue(null);
      // A run starting in a different chat session must not keep showing the
      // previous session's lifecycle (loading a session does not reset it).
      setLifecycleProgress((prev) =>
        prev && prev.sessionId && sessionId && prev.sessionId !== sessionId ? null : prev
      );
      // Resolve pending promise when execution starts
      if (pendingExecutionRef.current) {
        pendingExecutionRef.current.resolve({ id: executionId, session_id: sessionId });
        pendingExecutionRef.current = null;
        setPendingMessage(null);
      }
    },
    onIterationStarted: (iteration) => {
      console.log('[AIBuilderExecution] Iteration:', iteration);
      setAssistantActivity(createActivity(
        'iteration',
        iteration <= 1 ? 'Preparing the build' : 'Continuing the build',
        iteration <= 1
          ? 'The Architect is preparing the build handoff.'
          : 'The Builder is continuing the background workflow.',
      ));
    },
    onStatusUpdate: (status) => {
      setAssistantActivity(buildActivityFromStatus(status));
    },
    onToolCall: (toolName, toolArgs, _iteration, toolCallId, trace) => {
      console.log('[AIBuilderExecution] Tool call:', toolName, toolArgs, 'id:', toolCallId);
      setAssistantActivity(createActivity(
        'tool_call',
        formatToolActivity(toolName),
        'Checking the relevant data.',
      ));
      onToolCall?.(toolName, toolArgs, toolCallId, trace);
    },
    onToolResult: (toolName, result, _iteration, toolCallId, trace) => {
      console.log('[AIBuilderExecution] Tool result:', toolName, 'id:', toolCallId);
      setAssistantActivity(createActivity(
        result?.success === false ? 'tool_result_error' : 'tool_result',
        result?.success === false ? 'A background check needs review' : 'Background check completed',
        'Continuing the background orchestration.',
      ));
      onToolResult?.(toolName, result, toolCallId, trace);
    },
    onExecutionData: (data) => {
      // Surface only the real blocking issue from the durable runtime payload.
      // Lifecycle state comes exclusively from builder_lifecycle events.
      const issue = runtimeLastIssue(data.architectBuilderValidatorRuntime);
      if (issue) {
        setLastIssue((prev) => prev || issue);
      }
    },
    onLLMResponse: (content, iteration, isFinal) => {
      console.log('[AIBuilderExecution] LLM response:', content.substring(0, 50) + '...', 'final:', isFinal);
      if (isFinal) {
        setAssistantActivity(null);
      } else {
        setAssistantActivity(createActivity(
          'drafting',
          'Writing the response',
          iteration ? `Reasoning step ${iteration}.` : null,
        ));
      }
      onLLMResponse?.(content, isFinal);
    },
    onSubAgentEvent: (event) => {
      onSubAgentEvent?.(event);
    },
    onBuilderLifecycleEvent: (event) => {
      // The reducer performs the agent-room fan-out filter and adopts the
      // active session's lifecycle when the projection is stale (session
      // loads do not reset it).
      setLifecycleProgress((prev) => applyLifecycleEvent(prev, event, activeSessionIdRef.current));
    },
    onBuilderPlanSnapshot: (snapshot, context) => {
      setLifecycleProgress((prev) =>
        hydrateLifecycleFromSnapshot(prev, snapshot, context, activeSessionIdRef.current)
      );
    },
    onExecutionCompleted: (executionId, status, result) => {
      console.log('[AIBuilderExecution] Execution completed via WebSocket:', executionId, status);
      
      // Check if completion was already handled (to prevent duplicate from polling + WebSocket)
      if (handledCompletionIdsRef.current.has(executionId)) {
        console.log('[AIBuilderExecution] Completion already handled for:', executionId);
        return;
      }
      handledCompletionIdsRef.current.add(executionId);
      
      // Clear the restored execution ref and subscription tracking
      setAssistantActivity(null);
      restoredExecutionIdRef.current = null;
      hasSubscribedRef.current = null;
      // Stop polling (in case we were polling for a restored execution)
      stopPolling();
      onExecutionCompleted?.(executionId, status);
    },
    onError: (error) => {
      console.error('[AIBuilderExecution] Error:', error);
      setAssistantActivity(null);
      // Reject pending promise on error
      if (pendingExecutionRef.current) {
        pendingExecutionRef.current.reject(new Error(error));
        pendingExecutionRef.current = null;
        setPendingMessage(null);
      }
      onError?.(error);
    },
  });

  // Keep a ref to always have the latest socket (avoids stale closure issues)
  const socketRef = useRef(socket);
  socketRef.current = socket;

  // Keep the latest chat session ID available to lifecycle callbacks.
  activeSessionIdRef.current = socket.sessionId;

  // Execute via WebSocket
  const executeWithMessage = useCallback(async (
    message: string,
    sessionId?: string | null,
    context?: Record<string, any>,
    extraPayload?: Record<string, any>
  ): Promise<{ id: string; session_id?: string }> => {
    // Access socket through ref to get the latest
    const getSocket = () => socketRef.current;
    
    // Check actual WebSocket connection state (not React state)
    const isActuallyConnected = () => {
      const ws = getSocket().wsRef.current;
      return ws && ws.readyState === WebSocket.OPEN;
    };

    const wasConnected = isActuallyConnected();
    setPendingMessage(message);
    setLastIssue(null);
    setAssistantActivity(createActivity(
      wasConnected ? 'preparing' : 'connecting',
      wasConnected ? 'Preparing your request' : 'Connecting to Architect',
      wasConnected
        ? 'Preparing the Architect handoff.'
        : 'Your message will send as soon as the Architect reconnects.',
    ));
    
    // Check if connected using the actual WebSocket ref
    if (!wasConnected) {
      // Try to connect first (reset reconnect counter in case it was exhausted)
      console.log('[AIBuilderExecution] Not connected, attempting to connect...');
      getSocket().resetAndConnect();
      
      // Wait for connection
      try {
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('WebSocket connection timeout'));
          }, 5000);
          
          const checkConnection = setInterval(() => {
            // Check the actual WebSocket ref for connection status
            if (isActuallyConnected()) {
              clearInterval(checkConnection);
              clearTimeout(timeout);
              resolve();
            }
          }, 100);
        });
      } catch (error) {
        setPendingMessage(null);
        setAssistantActivity(null);
        throw error;
      }
    }

    // Final check before executing using actual WebSocket state
    const wsConnected = isActuallyConnected();
    console.log('[AIBuilderExecution] About to execute, wsRef connected:', wsConnected);
    
    if (!wsConnected) {
      throw new Error('WebSocket not connected after waiting');
    }
    
    // Create a promise that will be resolved when execution_started is received
    return new Promise((resolve, reject) => {
      pendingExecutionRef.current = { resolve, reject };

      // Worker queueing can legitimately take longer than a normal request.
      // Keep the request pending so the early session binding is preserved.
      const timeout = setTimeout(() => {
        if (pendingExecutionRef.current) {
          pendingExecutionRef.current.reject(new Error('Execution start timeout'));
          pendingExecutionRef.current = null;
          setPendingMessage(null);
          setAssistantActivity(null);
        }
      }, 5 * 60 * 1000);

      // Build the payload
      const payload: Record<string, any> = buildAgentExecutePayload({
        message, sessionId, systemId, context, extraPayload,
      });

      // Send directly using sendRaw to avoid stale closure issues
      console.log('[AIBuilderExecution] Sending execute payload via sendRaw');
      const sent = getSocket().sendRaw(payload);
      
      if (!sent) {
        clearTimeout(timeout);
        pendingExecutionRef.current = null;
        setPendingMessage(null);
        setAssistantActivity(null);
        reject(new Error('Failed to send message - WebSocket not connected'));
        return;
      }

      // Clear timeout when promise resolves/rejects
      const originalResolve = pendingExecutionRef.current.resolve;
      const originalReject = pendingExecutionRef.current.reject;
      
      pendingExecutionRef.current.resolve = (value) => {
        clearTimeout(timeout);
        originalResolve(value);
      };
      pendingExecutionRef.current.reject = (error) => {
        clearTimeout(timeout);
        originalReject(error);
      };
    });
  }, [systemId]);

  // Stop execution via WebSocket and API (both for reliability)
  const stopExecution = useCallback(async () => {
    const getSocket = () => socketRef.current;
    // Try to get execution ID from socket state, or fall back to our ref
    const execId = getSocket().executionId || restoredExecutionIdRef.current;
    
    if (!execId) {
      console.log('[AIBuilderExecution] No execution to stop');
      return;
    }
    
    console.log('[AIBuilderExecution] Stopping execution:', execId);
    
    // Try WebSocket first
    const sent = getSocket().sendRaw({
      type: 'stop',
      execution_id: execId,
    });
    
    console.log('[AIBuilderExecution] WebSocket stop sent:', sent);
    
    // ALWAYS also call API to ensure stop happens (WebSocket might not respond)
    try {
      console.log('[AIBuilderExecution] Also calling API to stop execution');
      await agentApi.stopExecution(execId);
      console.log('[AIBuilderExecution] Execution stopped via API');
      
      // Check if completion was already handled (WebSocket might have already fired)
      if (handledCompletionIdsRef.current.has(execId)) {
        console.log('[AIBuilderExecution] Stop completion already handled by WebSocket');
        return;
      }
      handledCompletionIdsRef.current.add(execId);
      
      // Clear the restored execution ref
      setAssistantActivity(null);
      restoredExecutionIdRef.current = null;
      // Stop any polling
      stopPolling();
      
      // Force stop the socket state immediately (don't wait for WebSocket)
      // This ensures the UI updates even if WebSocket doesn't respond
      socketRef.current.forceStopState();
      
      // Trigger completion callback
      onExecutionCompleted?.(execId, 'CANCELLED');
      
    } catch (error) {
      console.error('[AIBuilderExecution] Failed to stop execution via API:', error);
      onError?.('Failed to stop execution');
    }
  }, [onExecutionCompleted, onError, stopPolling]);
  
  const startPolling = useCallback((execId: string) => {
    // Clear any existing polling
    stopPolling();
    
    console.log('[AIBuilderExecution] Starting polling for execution:', execId);
    
    pollingIntervalRef.current = setInterval(async () => {
      // For restored executions, we MUST poll because WebSocket won't receive updates
      // (the execution was started on a different connection)
      // Don't stop polling just because WebSocket is connected
      
      try {
        const execution = await agentApi.getExecutionSummary(execId);
        
        if (execution.status !== 'RUNNING') {
          console.log('[AIBuilderExecution] Polling detected completion:', execution.status);
          stopPolling();
          
          // Check if completion was already handled (to prevent duplicate from polling + WebSocket)
          if (handledCompletionIdsRef.current.has(execId)) {
            console.log('[AIBuilderExecution] Completion already handled by WebSocket for:', execId);
            return;
          }
          handledCompletionIdsRef.current.add(execId);
          
          // Force stop the socket state since this was a restored execution
          socketRef.current.forceStopState();
          onExecutionCompleted?.(execId, execution.status);
        }
      } catch (error) {
        console.error('[AIBuilderExecution] Polling error:', error);
      }
    }, 2000); // Poll every 2 seconds
  }, [onExecutionCompleted, stopPolling]);

  // Subscribe to existing execution (for page reload scenarios with running executions)
  // The backend now auto-subscribes on reconnect, so WebSocket streaming should work.
  // We still start polling as a fallback in case the backend auto-subscribe didn't happen
  // (e.g. server restarted, execution on different instance).
  const subscribeToExecution = useCallback(async (execId: string) => {
    console.log('[AIBuilderExecution] Subscribe to execution:', execId);
    
    // Fetch current execution status from API
    try {
      const execution = await agentApi.getExecutionSummary(execId);
      
      if (execution.status === 'RUNNING') {
        console.log('[AIBuilderExecution] Execution still running, restoring state');
        setAssistantActivity(createActivity(
          'running',
          'Resuming the Architect run',
          'Loading the current execution state.',
        ));
        
        // Store in ref for immediate access (avoids stale closure issues)
        restoredExecutionIdRef.current = execId;
        
        // Set the socket state directly via ref to indicate we're tracking this execution
        socketRef.current.restore(execId, execution.session_id || null);
        
        // For restored executions, we MUST poll since WebSocket won't receive updates
        // (the execution was started on a different WebSocket connection)
        console.log('[AIBuilderExecution] Starting polling for restored execution');
        startPolling(execId);
      } else {
        console.log('[AIBuilderExecution] Execution already completed:', execution.status);
        onExecutionCompleted?.(execId, execution.status);
      }
    } catch (error) {
      console.error('[AIBuilderExecution] Failed to fetch execution:', error);
      onError?.('Failed to restore execution state');
    }
  }, [onExecutionCompleted, onError, startPolling]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Reset state
  const reset = useCallback(() => {
    console.log('[AIBuilderExecution] Resetting state');
    socketRef.current.reset();
    pendingExecutionRef.current = null;
    restoredExecutionIdRef.current = null;
    hasSubscribedRef.current = null;
    setPendingMessage(null);
    setAssistantActivity(null);
    setLastIssue(null);
    setLifecycleProgress(null);
    stopPolling();
  }, [stopPolling]);

  // Calculate progress based on current iteration and max iterations
  const progress = socket.currentIteration > 0 
    ? Math.min((socket.currentIteration / socket.maxIterations) * 100, 99) 
    : 0;
  const isAwaitingExecutionStart = pendingMessage !== null && !socket.isExecuting;

  return {
    isConnected: socket.isConnected,
    isExecuting: socket.isExecuting,
    isAwaitingExecutionStart,
    // Return execution ID from socket or from our ref (for restored executions)
    currentExecutionId: socket.executionId || restoredExecutionIdRef.current,
    currentSessionId: socket.sessionId,
    currentExecutionPlan: socket.executionPlan,
    executionStatus: socket.status,
    progress,
    currentIteration: socket.currentIteration,
    maxIterations: socket.maxIterations,
    expertProgress,
    error: socket.error,
    totalCost: socket.totalCost,
    totalTokens: socket.totalTokens,
    toolNames: socket.toolNames,
    executionResult: socket.result,
    assistantActivity,
    lastIssue,
    lifecycleProgress,
    executeWithMessage,
    stopExecution,
    subscribeToExecution,
    reset,
    connect: socket.connect,
    disconnect: socket.disconnect,
    pendingConfirmation: socket.pendingConfirmation,
    confirmTool: socket.confirmTool,
  };
}

export default useAIBuilderExecution;
